export const GROUP_TYPE = {
  SINGLE: "Single Group",
  MULTIPLE: "Multiple Group",
};

const pick = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

export const SINGLE_STEPS = [
  { id: "setup", label: "Setup", short: "1" },
  { id: "visits", label: "Visits", short: "2" },
  { id: "publish", label: "Go live", short: "3" },
  { id: "activity", label: "Activity", short: "4" },
];

export const MULTI_STEPS = [
  { id: "setup", label: "Setup", short: "1" },
  { id: "groups-visits", label: "Groups & Visits", short: "2" },
  { id: "publish", label: "Go live", short: "3" },
  { id: "activity", label: "Activity", short: "4" },
];

export const SETUP_TOGGLES = [
  {
    key: "studyScreening",
    label: "Study screening",
    hint: "Enable screening visit configuration for this study.",
    offImpact:
      "This schedule will have no screening visit. Participants go straight to treatment visits.",
  },
  {
    key: "screeningRandomisationTogether",
    label: "Screening and randomisation together",
    hint: "Combine screening and randomisation in one flow.",
    dependsOn: "studyScreening",
    offImpact:
      "Screening and randomisation stay separate. Randomisation cost is not collected with the screening visit.",
  },
  {
    key: "visitPayment",
    label: "Visit payment setup",
    hint: "Configure participant visit payments.",
    offImpact:
      "You will not be able to pay participants on this schedule.",
  },
  {
    key: "visitCost",
    label: "Visit cost setup",
    hint: "Track visit costs for budgeting.",
    offImpact:
      "You will not be able to run cost analysis for this schedule.",
  },
  {
    key: "eCommunication",
    label: "E-communication setup",
    hint: "Send automated communications per visit.",
    offImpact:
      "No booking or reminder emails/SMS will be sent for visits on this schedule.",
  },
  {
    key: "studyWorksheet",
    label: "Study worksheet",
    hint: "Generate worksheets for site staff.",
    offImpact:
      "Site staff will not receive schedule worksheets or visit documents from this schedule.",
  },
];

export function getSetupToggle(key) {
  return SETUP_TOGGLES.find((toggle) => toggle.key === key) || null;
}

export function defaultSetupFlags() {
  return {
    studyScreening: true,
    screeningRandomisationTogether: true,
    visitPayment: true,
    visitCost: true,
    eCommunication: true,
    studyWorksheet: true,
  };
}

export function defaultWizardState() {
  return {
    groupType: GROUP_TYPE.SINGLE,
    setupFlags: defaultSetupFlags(),
    studyColor: "#2563eb",
    noOfVisits: 1,
    visitDrafts: [],
    groupVisitDrafts: {},
    runInPeriod: false,
    noOfGroups: 2,
    allGroupsSameSchedule: true,
    activeGroupNameId: null,
    groupNames: [],
  };
}

export function getStepsForGroupType(groupType) {
  return groupType === GROUP_TYPE.MULTIPLE ? MULTI_STEPS : SINGLE_STEPS;
}

export function validateVisitsStep(noOfVisits) {
  const n = Number(noOfVisits);
  if (!Number.isFinite(n) || n < 1 || n > 99) {
    return { valid: false, message: "Enter between 1 and 99 visits." };
  }
  return { valid: true, value: Math.floor(n) };
}

export function resolveCohortSaveStudyId({
  trialStudyId,
  studyScheduleId,
  studyFromNav = null,
} = {}) {
  return (
    studyScheduleId ||
    pick(studyFromNav, "StudyScheduleId", "studyScheduleId") ||
    trialStudyId ||
    ""
  );
}

export { validateVisitDrafts } from "./scheduleVisitDraft";

export function validateGroupsStep(noOfGroups) {
  const n = Number(noOfGroups);
  if (!Number.isFinite(n) || n < 2 || n > 20) {
    return { valid: false, message: "Enter between 2 and 20 groups." };
  }
  return { valid: true, value: Math.floor(n) };
}

export const MIN_MULTI_TREATMENT_GROUPS = 2;

/** Visits created on first setup save: Screening + Visit 1 when screening on, else Visit 1 only. */
export function resolveSetupSaveVisitCount(setupFlags = {}) {
  return setupFlags.studyScreening ? 2 : 1;
}

export function buildSingleGroupPayload({
  studyId,
  organizationId,
  createdBy,
  studyColor,
  setupFlags,
  noOfVisits,
  action,
  comments,
}) {
  return {
    StudyId: studyId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    StudyColor: studyColor,
    GroupType: GROUP_TYPE.SINGLE,
    Action: action || "Create",
    NoOfVisits: Number(noOfVisits) || 0,
    StudyScreening: !!setupFlags.studyScreening,
    ScreeningrandomisationTogether: !!setupFlags.screeningRandomisationTogether,
    IsVisitPayment: !!setupFlags.visitPayment,
    IsVisitCost: !!setupFlags.visitCost,
    IsDepartmentRoleMapping: !!setupFlags.departmentRoleMapping,
    IsCommunicationSetup: !!setupFlags.eCommunication,
    IsStudyWorkSheet: !!setupFlags.studyWorksheet,
    ...(comments ? { Comments: comments } : {}),
  };
}

export function buildMultiGroupStep1Payload({
  studyId,
  organizationId,
  createdBy,
  studyColor,
  setupFlags,
  comments,
}) {
  return {
    StudyId: studyId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    StudyColor: studyColor,
    StudyScreening: !!setupFlags.studyScreening,
    StudyScreeningRandomisationTogether: !!setupFlags.screeningRandomisationTogether,
    IsVisitPayment: !!setupFlags.visitPayment,
    IsVisitCost: !!setupFlags.visitCost,
    IsDepartmentRoleMapping: !!setupFlags.departmentRoleMapping,
    IsCommunicationSetup: !!setupFlags.eCommunication,
    IsStudyWorkSheet: !!setupFlags.studyWorksheet,
    ...(comments ? { Comments: comments } : {}),
  };
}

export function buildMultiGroupStep2Payload({
  studyId,
  studyScheduleId,
  studyGroupId,
  organizationId,
  createdBy,
  noOfGroups,
  allGroupsSameSchedule,
  comments,
}) {
  return {
    StudyId: studyId,
    StudyScheduleId: studyScheduleId,
    StudyGroupId: studyGroupId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    NoOfGroup: Number(noOfGroups),
    AllGroupSameSchedule: !!allGroupsSameSchedule,
    ...(comments ? { Comments: comments } : {}),
  };
}

export function buildMultiGroupStep3Payload({
  studyId,
  studyScheduleId,
  organizationId,
  createdBy,
  studyGroupId,
  groupNameId,
  noOfVisits,
  setupFlags,
  comments,
}) {
  return {
    StudyId: studyId,
    StudyScheduleId: studyScheduleId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    StudyGroupId: studyGroupId,
    GroupNameId: groupNameId || null,
    NoOfVisits: Number(noOfVisits),
    GroupScreening: false,
    ScreeningrandomisationTogether: !!setupFlags.screeningRandomisationTogether,
    ...(comments ? { Comments: comments } : {}),
  };
}

export function flagsFromStudyRow(studyRow = {}, groupNameRow = {}) {
  return {
    studyScreening: !!(
      studyRow.StudyScreening ??
      studyRow.studyScreening ??
      studyRow.IsStudyScreening ??
      studyRow.isStudyScreening ??
      groupNameRow.GroupScreening ??
      groupNameRow.groupScreening
    ),
    screeningRandomisationTogether: !!(
      studyRow.StudyScreeningRandomisationTogether ??
      studyRow.studyScreeningRandomisationTogether ??
      groupNameRow.ScreeningrandomisationTogether ??
      groupNameRow.screeningrandomisationTogether ??
      studyRow.ScreeningrandomisationTogether ??
      studyRow.screeningrandomisationTogether
    ),
    visitPayment: !!(
      studyRow.IsVisitPayment ??
      studyRow.isVisitPayment ??
      studyRow.VisitPayment ??
      studyRow.visitPayment
    ),
    visitCost: !!(studyRow.IsVisitCost ?? studyRow.isVisitCost ?? studyRow.VisitCost ?? studyRow.visitCost),
    departmentRoleMapping: !!(
      studyRow.IsDepartmentRoleMapping ??
      studyRow.isDepartmentRoleMapping
    ),
    eCommunication: !!(
      studyRow.IsCommunicationSetup ??
      studyRow.isCommunicationSetup
    ),
    studyWorksheet: !!(studyRow.IsStudyWorkSheet ?? studyRow.isStudyWorkSheet),
  };
}

export function setupFlagsToStudyRow(setupFlags = {}) {
  return {
    StudyScreening: !!setupFlags.studyScreening,
    IsStudyScreening: !!setupFlags.studyScreening,
    ScreeningrandomisationTogether: !!setupFlags.screeningRandomisationTogether,
    StudyScreeningRandomisationTogether: !!setupFlags.screeningRandomisationTogether,
    IsVisitPayment: !!setupFlags.visitPayment,
    IsVisitCost: !!setupFlags.visitCost,
    IsDepartmentRoleMapping: !!setupFlags.departmentRoleMapping,
    IsCommunicationSetup: !!setupFlags.eCommunication,
    IsStudyWorkSheet: !!setupFlags.studyWorksheet,
  };
}
